import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { YStack } from 'tamagui';
import { AuthProvider } from '@shared/auth';
import { MainNav, Footer } from '@shared/components';
import { Home, Blog, Login, Signup, Profile, Admin, AdminDebug, FileExplorer, ShareViewer, JobQueue } from '@shared/pages';
import React from 'react';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('ErrorBoundary caught:', error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <YStack flex={1} padding="$4" backgroundColor="#fff" justifyContent="center" alignItems="center">
          <div style={{ color: 'red', fontSize: '16px', textAlign: 'center' }}>
            <h1>Error occurred</h1>
            <p>{this.state.error?.message}</p>
            <pre>{this.state.error?.stack}</pre>
          </div>
        </YStack>
      );
    }
    return this.props.children;
  }
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  console.log('AppContent rendered');

  // Define pages where footer should NOT be displayed
  const pagesWithoutFooter = ['/login', '/signup'];
  const showFooter = !pagesWithoutFooter.includes(location.pathname);

  return (
    <YStack flex={1} minHeight="100vh" display="flex" flexDirection="column">
      <MainNav 
        onNavigate={(route) => navigate(route)} 
        onLogout={() => navigate('/')}
      />
      <YStack flex={1}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/blog" element={<Blog />} />
          <Route
            path="/login"
            element={
              <Login
                onNavigateToSignup={() => navigate('/signup')}
                onLoginSuccess={() => {
                  console.log('Login successful, navigating to home');
                  navigate('/');
                }}
              />
            }
          />
          <Route
            path="/signup"
            element={
              <Signup
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />
          <Route
            path="/profile"
            element={
              <Profile
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />
          <Route
            path="/jobs"
            element={
              <JobQueue
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />
          <Route
            path="/admin"
            element={
              <Admin
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />
          <Route
            path="/admin-debug"
            element={
              <AdminDebug />
            }
          />
          <Route
            path="/files"
            element={
              <FileExplorer
                onNavigateToLogin={() => navigate('/login')}
              />
            }
          />
          <Route
            path="/share/:shareId"
            element={<ShareViewer />}
          />
        </Routes>
      </YStack>
      <Footer showFooter={showFooter} />
    </YStack>
  );
}

function App() {
  console.log('App rendered');
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
